"use client";

import {JSX} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {AttractionContactInfo} from "@/entities/destination/attraction/types";
import {SchedulePrices} from "@/entities/destination/attraction/ui/schedule-prices";

import {HtmlContent} from "@/shared/ui/data-display/html-content";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {Breadcrumbs} from "@/shared/ui/navigation/breadcrumbs";
import {assetPath} from "@/shared/utils/asset-path";

import {GallerySlider} from "./gallery-slider";

import "./things-to-do-details.scss";

export type ThingsToDoDetailsProps = {
    title?: string;
    contact_info?: AttractionContactInfo;
    images?: Array<{src: string; alt: string}>;
    overview?: string | null;
    duration?: string | null;
    schedule?: string | null;
    scheduleAndPrices?: string | null;
    breadcrumbs?: Array<{label: string; href?: string}>;
    space?: SpaceProps;
    translations?: {
        attraction?: string;
        overview?: string;
        visitWebsite?: string;
    };
};

export function ThingsToDoDetails({
    title,
    contact_info,
    images = [],
    overview,
    duration,
    schedule,
    scheduleAndPrices,
    breadcrumbs = [],
    space,
    translations = {},
}: ThingsToDoDetailsProps): JSX.Element {
    const sectionSpace = useSectionSpace(space);

    return (
        <section className="things-to-do-details" style={sectionSpace}>
            <ContainerBase>
                <Breadcrumbs separator=" > " items={breadcrumbs} />
                <div className="ttd-head">
                    <div className="ttd-head__titles">
                        <Typography as="h1" size={{desktop: 24, mobile: 20}} weight={{desktop: 500, mobile: 600}} color="title">
                            {title || translations.attraction || "Attraction"}
                        </Typography>
                        {!!contact_info?.address && (
                            <Typography as="p" size={{desktop: 12, mobile: 12}} color="paragraph">
                                {contact_info?.address}
                            </Typography>
                        )}
                    </div>
                </div>

                <div className="ttd-body">
                    <div className="ttd-body__left">
                        <GallerySlider images={images} />
                    </div>

                    <div className="ttd-body__right">
                        {overview && (
                            <div className="ttd-box">
                                <Typography as="h3" size={16} weight={500} color="title" className="ttd-box__title">
                                    {translations.overview || "Overview"}
                                </Typography>
                                <HtmlContent html={overview} />

                                {(duration || schedule) && (
                                    <div className="ttd-icons">
                                        {duration && (
                                            <div className="ttd-icon">
                                                <SmartImage
                                                    src={assetPath("/images/things-to-do/icons/clock.svg")}
                                                    alt="Duration"
                                                    width={18}
                                                    height={18}
                                                />
                                                <Typography as="span" size={{desktop: 14, mobile: 12}} color="title">
                                                    {duration}
                                                </Typography>
                                            </div>
                                        )}
                                        {schedule && (
                                            <div className="ttd-icon">
                                                <SmartImage
                                                    src={assetPath("/images/things-to-do/icons/timer.svg")}
                                                    alt="Schedule"
                                                    width={18}
                                                    height={18}
                                                />
                                                <Typography as="span" size={{desktop: 14, mobile: 12}} color="title">
                                                    {schedule}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {scheduleAndPrices && (
                            <div className="ttd-box">
                                <SchedulePrices html={scheduleAndPrices} />
                            </div>
                        )}

                        {(contact_info?.phone || contact_info?.website) && (
                            <div className="ttd-box">
                                <div className="ttd-contact">
                                    {contact_info.phone && (
                                        <div className="ttd-contact__item">
                                            <SmartImage src={assetPath("/images/icons/call.svg")} alt="Phone" width={20} height={20} />
                                            <a href={`tel:${contact_info.phone}`} className="ttd-contact__link">
                                                <Typography as="span" size={14} weight={600} color="title">
                                                    {contact_info.phone}
                                                </Typography>
                                            </a>
                                        </div>
                                    )}
                                    {contact_info.website && (
                                        <div className="ttd-contact__item">
                                            <SmartImage src={assetPath("/images/icons/global.svg")} alt="Website" width={20} height={20} />
                                            <a href={contact_info.website} target="_blank" rel="noopener noreferrer" className="ttd-contact__link">
                                                <Typography as="span" size={14} weight={600} color="title">
                                                    {translations.visitWebsite}
                                                </Typography>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </ContainerBase>
        </section>
    );
}
